/**
 * Customer Authentication Context — trimmed, single-tenant port of
 * frontend/src/context/CustomerAuthContext.js for the Noosa storefront's account dashboard.
 * Email/password only (no Mango SSO, no code-based signup) — those can be added later if Noosa
 * wants them, see docs/NOOSA-ORDER-INTAKE-TASKS.md.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS, resolveShopBusinessId } from '../config/api';

const CustomerAuthContext = createContext();

const assertTenantConfigured = async () => {
  const id = await resolveShopBusinessId();
  if (!id) {
    throw new Error('Could not resolve this store — please try again shortly.');
  }
  return id;
};

/** Decode JWT payload (no verify) — used to check expiry client-side. */
const readJwtPayload = (token) => {
  try {
    const parts = String(token).split('.');
    if (parts.length < 2) return null;
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    if (typeof atob !== 'function') return null;
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
};

const isJwtExpired = (token) => {
  const p = readJwtPayload(token);
  if (!p) return true;
  if (!p.exp) return false;
  return Date.now() / 1000 > Number(p.exp);
};

export const CustomerAuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const savedToken = localStorage.getItem('customer_token');
    const savedCustomer = localStorage.getItem('customer_data');

    if (savedToken && !isJwtExpired(savedToken)) {
      setToken(savedToken);
      if (savedCustomer) {
        try {
          setCustomer(JSON.parse(savedCustomer));
        } catch {
          // ignore corrupt cache; refreshCustomer() below repopulates once loaded
        }
      }
    } else if (savedToken) {
      localStorage.removeItem('customer_token');
      localStorage.removeItem('customer_data');
    }
    setIsLoading(false);
  }, []);

  const signup = async ({ firstName, lastName, email, password, phone }) => {
    setIsLoading(true);
    try {
      const businessId = await assertTenantConfigured();
      const response = await fetch(API_ENDPOINTS.customerAuth.signup, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firstName, lastName, email, password, phone, businessId }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Signup failed');
      setToken(data.token);
      setCustomer(data.customer);
      localStorage.setItem('customer_token', data.token);
      localStorage.setItem('customer_data', JSON.stringify(data.customer));
      return data.customer;
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email, password) => {
    setIsLoading(true);
    try {
      const businessId = await assertTenantConfigured();
      const response = await fetch(API_ENDPOINTS.customerAuth.login, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, businessId }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Login failed');
      setToken(data.token);
      setCustomer(data.customer);
      localStorage.setItem('customer_token', data.token);
      localStorage.setItem('customer_data', JSON.stringify(data.customer));
      return data.customer;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setCustomer(null);
    setToken(null);
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer_data');
  };

  const getAuthHeaders = useCallback(() => {
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  }, [token]);

  const refreshCustomer = useCallback(async () => {
    if (!token) return null;
    try {
      const response = await fetch(API_ENDPOINTS.customerProfile.me, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to fetch customer data');
      const data = await response.json();
      setCustomer(data.customer);
      localStorage.setItem('customer_data', JSON.stringify(data.customer));
      return data.customer;
    } catch (error) {
      if (String(error.message).includes('401') || String(error.message).includes('403')) logout();
      return null;
    }
  }, [token, getAuthHeaders]);

  useEffect(() => {
    if (token) refreshCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <CustomerAuthContext.Provider
      value={{
        customer,
        token,
        isLoading,
        isAuthenticated: !!customer && !!token,
        signup,
        login,
        logout,
        getAuthHeaders,
        refreshCustomer,
      }}
    >
      {children}
    </CustomerAuthContext.Provider>
  );
};

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used within CustomerAuthProvider');
  }
  return context;
};
